/** XeHirePro - AI-Powered Recruitment Platform Landing Page */

import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sparkles,
  FileText,
  Phone,
  Calendar,
  Users,
  BarChart3,
  CheckCircle,
  ArrowRight,
  Play,
  Zap,
  Clock,
  Target,
  Award,
  Brain,
  MessageSquare,
  Video,
  Star,
  ChevronRight,
  Upload,
  Bot,
  TrendingUp,
  Shield,
} from "lucide-react";
import { AuthDialogProvider, useAuthDialog } from "@/components/landing/AuthDialog";

function HeroSection() {
  const { openAuth } = useAuthDialog();

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 dark:from-gray-900 dark:via-purple-950 dark:to-gray-900 pt-20 pb-32">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-pink-300/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-300/20 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300 px-4 py-1.5">
            <Sparkles className="w-4 h-4 mr-2" />
            AI-Powered Recruitment Platform
          </Badge>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
            Hire Smarter,
            <br />
            Faster with AI
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            From resume screening to final interviews - automate your entire recruitment pipeline with intelligent AI agents.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              size="lg"
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-lg px-8 py-6"
              onClick={() => openAuth('register')}
            >
              Start Hiring Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6"
            >
              <Play className="mr-2 h-5 w-5" />
              Watch Demo
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {[
              { value: "90%", label: "Time Saved" },
              { value: "3x", label: "Faster Hiring" },
              { value: "85%", label: "Better Matches" },
              { value: "24/7", label: "AI Screening" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-pink-600 dark:text-pink-400">{stat.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    {
      number: "01",
      icon: Upload,
      title: "Upload Job Description",
      description: "Simply upload your JD and our AI extracts requirements, skills, and creates screening criteria automatically.",
      color: "from-pink-500 to-rose-500",
    },
    {
      number: "02",
      icon: FileText,
      title: "AI Screens Resumes",
      description: "AI analyzes CVs against job requirements, scores candidates, and identifies the best matches instantly.",
      color: "from-purple-500 to-violet-500",
    },
    {
      number: "03",
      icon: Phone,
      title: "AI Conducts Screening Calls",
      description: "Voice AI automatically calls candidates, verifies interest, and conducts initial screening interviews.",
      color: "from-indigo-500 to-blue-500",
    },
    {
      number: "04",
      icon: Calendar,
      title: "Schedule Interviews",
      description: "Qualified candidates are automatically scheduled for interviews on Teams or Google Meet.",
      color: "from-blue-500 to-cyan-500",
    },
    {
      number: "05",
      icon: Brain,
      title: "AI Evaluates & Ranks",
      description: "After interviews, AI evaluates responses and ranks candidates to help you make the best hiring decision.",
      color: "from-teal-500 to-emerald-500",
    },
  ];

  return (
    <section className="py-24 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
            How It Works
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            5 Steps to Your Perfect Hire
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Our AI handles the entire recruitment process, from job posting to final candidate ranking.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className={`flex items-start gap-8 mb-12 ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}
            >
              <div className={`flex-shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                <step.icon className="w-10 h-10 text-white" />
              </div>
              <div className={`flex-1 ${index % 2 === 1 ? 'text-right' : ''}`}>
                <div className="text-sm font-bold text-pink-500 mb-1">STEP {step.number}</div>
                <h3 className="text-2xl font-bold mb-2">{step.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-lg">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: FileText,
      title: "Smart Resume Parsing",
      description: "AI extracts skills, experience, education, and qualifications from any resume format.",
      color: "bg-pink-100 text-pink-600 dark:bg-pink-900 dark:text-pink-400",
    },
    {
      icon: Bot,
      title: "AI Screening Calls",
      description: "Voice AI conducts initial screening calls, asking relevant questions and evaluating responses.",
      color: "bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400",
    },
    {
      icon: Target,
      title: "Skill Matching",
      description: "Intelligent matching algorithm compares candidate skills against job requirements.",
      color: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-400",
    },
    {
      icon: Video,
      title: "Interview Scheduling",
      description: "Automatic scheduling with Microsoft Teams and Google Meet integration.",
      color: "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400",
    },
    {
      icon: BarChart3,
      title: "Candidate Ranking",
      description: "AI-powered ranking based on resume match, interview performance, and overall fit.",
      color: "bg-teal-100 text-teal-600 dark:bg-teal-900 dark:text-teal-400",
    },
    {
      icon: MessageSquare,
      title: "Interview Evaluation",
      description: "AI analyzes interview transcripts and provides detailed evaluation reports.",
      color: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-400",
    },
    {
      icon: TrendingUp,
      title: "Multi-Round Support",
      description: "Handle technical, behavioral, HR, and custom interview rounds seamlessly.",
      color: "bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400",
    },
    {
      icon: Shield,
      title: "Bias-Free Screening",
      description: "Objective, consistent evaluation criteria across all candidates.",
      color: "bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400",
    },
  ];

  return (
    <section className="py-24 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300">
            Features
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Everything You Need to Hire Better
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Powerful AI tools that transform your recruitment process from start to finish.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {features.map((feature) => (
            <Card key={feature.title} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCasesSection() {
  const useCases = [
    {
      title: "High-Volume Hiring",
      description: "Screen thousands of applications automatically and identify top candidates in minutes, not weeks.",
      metrics: ["10,000+ resumes/hour", "90% time reduction", "Zero missed candidates"],
      icon: Users,
    },
    {
      title: "Technical Recruiting",
      description: "AI evaluates technical skills, coding experience, and framework expertise accurately.",
      metrics: ["Skill verification", "Experience validation", "Tech stack matching"],
      icon: Brain,
    },
    {
      title: "Executive Search",
      description: "Conduct confidential, professional screening calls for senior positions.",
      metrics: ["Discrete outreach", "Cultural fit analysis", "Leadership assessment"],
      icon: Award,
    },
    {
      title: "Campus Recruiting",
      description: "Efficiently screen and schedule interviews with large volumes of graduate candidates.",
      metrics: ["Bulk processing", "Fair evaluation", "Quick turnaround"],
      icon: Target,
    },
  ];

  return (
    <section className="py-24 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
            Use Cases
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Built for Every Hiring Challenge
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {useCases.map((useCase) => (
            <Card key={useCase.title} className="border-2 hover:border-pink-300 dark:hover:border-pink-700 transition-colors">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                    <useCase.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{useCase.title}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.metrics.map((metric) => (
                    <Badge key={metric} variant="secondary" className="text-xs">
                      {metric}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const { openAuth } = useAuthDialog();

  const plans = [
    {
      name: "Starter",
      price: "$99",
      period: "/month",
      description: "Perfect for small teams",
      features: [
        "Up to 50 candidates/month",
        "AI Resume Screening",
        "Basic Candidate Ranking",
        "Email Support",
      ],
      cta: "Start Free Trial",
      popular: false,
    },
    {
      name: "Professional",
      price: "$299",
      period: "/month",
      description: "For growing companies",
      features: [
        "Up to 500 candidates/month",
        "AI Screening Calls",
        "Interview Scheduling",
        "Multi-Round Interviews",
        "Advanced Analytics",
        "Priority Support",
      ],
      cta: "Start Free Trial",
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large organizations",
      features: [
        "Unlimited candidates",
        "Custom AI Models",
        "ATS Integration",
        "SSO & Security",
        "Dedicated Success Manager",
        "Custom Branding",
      ],
      cta: "Contact Sales",
      popular: false,
    },
  ];

  return (
    <section className="py-24 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300">
            Pricing
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Start free, scale as you grow
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative ${
                plan.popular
                  ? 'border-2 border-pink-500 shadow-xl scale-105'
                  : 'border shadow-lg'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-1">
                    Most Popular
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center pt-8">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-gray-500">{plan.period}</span>
                </div>
                <CardDescription className="mt-2">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full ${
                    plan.popular
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700'
                      : ''
                  }`}
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => openAuth('register')}
                >
                  {plan.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { openAuth } = useAuthDialog();

  return (
    <section className="py-24 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to Transform Your Hiring?
        </h2>
        <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
          Join hundreds of companies already using XeHirePro to hire smarter and faster.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            size="lg"
            className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8"
            onClick={() => openAuth('register')}
          >
            Start Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-white text-white hover:bg-white/10 text-lg px-8"
          >
            Schedule Demo
          </Button>
        </div>
      </div>
    </section>
  );
}

function Navbar() {
  const [, setLocation] = useLocation();
  const { openAuth } = useAuthDialog();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setLocation('/')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
              XeHirePro
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-gray-600 dark:text-gray-300 hover:text-pink-600">Features</a>
            <a href="#how-it-works" className="text-gray-600 dark:text-gray-300 hover:text-pink-600">How It Works</a>
            <a href="#pricing" className="text-gray-600 dark:text-gray-300 hover:text-pink-600">Pricing</a>
          </div>

          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => openAuth('login')}>
              Sign In
            </Button>
            <Button
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
              onClick={() => openAuth('register')}
            >
              Get Started
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">XeHirePro</span>
            </div>
            <p className="text-gray-400 text-sm">
              AI-powered recruitment platform that transforms how companies hire talent.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#features" className="hover:text-white">Features</a></li>
              <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
              <li><a href="#" className="hover:text-white">Integrations</a></li>
              <li><a href="#" className="hover:text-white">API</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-white">About</a></li>
              <li><a href="#" className="hover:text-white">Blog</a></li>
              <li><a href="#" className="hover:text-white">Careers</a></li>
              <li><a href="#" className="hover:text-white">Contact</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
              <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
              <li><a href="/cookies" className="hover:text-white">Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} XeHirePro by Xenonlabs.ai. All rights reserved.
          </p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <a href="#" className="text-gray-400 hover:text-white">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-white">LinkedIn</a>
            <a href="#" className="text-gray-400 hover:text-white">GitHub</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function XeHireProLanding() {
  return (
    <AuthDialogProvider>
      <div className="min-h-screen bg-background">
        <Navbar />
        <main>
          <HeroSection />
          <section id="how-it-works">
            <HowItWorksSection />
          </section>
          <section id="features">
            <FeaturesSection />
          </section>
          <UseCasesSection />
          <section id="pricing">
            <PricingSection />
          </section>
          <CTASection />
        </main>
        <Footer />
      </div>
    </AuthDialogProvider>
  );
}
